import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';

const MAX_IMAGE_SIZE = 2 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'];
const USER_MAPS_KEY = 'votenations_user_maps';

function OptionImageUpload({ id, image, error, onUpload }) {
  return (
    <div className="space-y-2">
      <label htmlFor={id} className="block text-sm text-slate-400">
        Flag / Image
      </label>
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded border border-slate-700 bg-slate-950 flex items-center justify-center overflow-hidden shrink-0">
          {image ? (
            <img src={image} alt="Option preview" className="w-full h-full object-cover" />
          ) : (
            <span className="text-[10px] text-slate-600 uppercase font-bold">None</span>
          )}
        </div>
        <input
          id={id}
          type="file"
          accept="image/png,image/jpeg,image/svg+xml"
          className="text-sm text-slate-400 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-800 file:text-white file:cursor-pointer hover:file:bg-slate-700"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onUpload(file);
            e.target.value = '';
          }}
        />
      </div>
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
}

export default function CreateMap() {
  const { user, loading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    region: 'South America',
    optionAName: '',
    optionAColor: '#39FF14',
    optionAImage: null,
    optionBName: '',
    optionBColor: '#FF007F',
    optionBImage: null,
  });
  const [imageErrors, setImageErrors] = useState({ optionA: '', optionB: '' });

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center text-slate-400">
        {t('loading')}
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">{t('auth_required')}</h2>
          <button onClick={() => navigate('/login')} className="px-6 py-2 bg-lime-400 text-slate-950 font-bold rounded">
            {t('login')}
          </button>
        </div>
      </div>
    );
  }

  const handleImageUpload = (option, file) => {
    const errorKey = option === 'A' ? 'optionA' : 'optionB';
    const imageKey = option === 'A' ? 'optionAImage' : 'optionBImage';

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setImageErrors((prev) => ({
        ...prev,
        [errorKey]: 'Invalid file type. Use PNG, JPEG, or SVG.',
      }));
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      setImageErrors((prev) => ({
        ...prev,
        [errorKey]: 'Image must be 2MB or smaller.',
      }));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setFormData((prev) => ({ ...prev, [imageKey]: reader.result }));
      setImageErrors((prev) => ({ ...prev, [errorKey]: '' }));
    };
    reader.onerror = () => {
      setImageErrors((prev) => ({
        ...prev,
        [errorKey]: 'Failed to read image file.',
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const newMap = {
      id: `user-${Date.now()}`,
      title: formData.title,
      region: formData.region,
      defaultMemeText: '',
      optionA: {
        name: formData.optionAName,
        color: formData.optionAColor,
        image: formData.optionAImage,
      },
      optionB: {
        name: formData.optionBName,
        color: formData.optionBColor,
        image: formData.optionBImage,
      },
      votesA: 0,
      votesB: 0,
      countryVotes: {},
    };

    const existing = JSON.parse(localStorage.getItem(USER_MAPS_KEY) || '[]');
    localStorage.setItem(USER_MAPS_KEY, JSON.stringify([...existing, newMap]));

    alert(t('map_created') + ' (Mock Pass 1)');
    navigate('/');
  };

  return (
    <div className="max-w-2xl mx-auto p-4 py-12">
      <div className="bg-slate-900 p-8 rounded-2xl border border-slate-800 shadow-2xl">
        <h1 className="text-3xl font-black uppercase italic mb-8">{t('create_new_battle')}</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-bold text-slate-400 mb-2 uppercase">Battle Title</label>
            <input
              required
              type="text"
              className="w-full bg-slate-950 border border-slate-700 rounded p-3 text-white focus:border-lime-400 outline-none transition-colors"
              placeholder="e.g. Pineapples on Pizza"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-400 mb-2 uppercase">{t('region')}</label>
            <select
              className="w-full bg-slate-950 border border-slate-700 rounded p-3 text-white focus:border-lime-400 outline-none"
              value={formData.region}
              onChange={(e) => setFormData({ ...formData, region: e.target.value })}
            >
              <option>World</option>
              <option>Americas</option>
              <option>South America</option>
              <option>Latin America</option>
              <option>Europe</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-6 pt-4 border-t border-slate-800">
            <div className="space-y-4">
              <h3 className="font-bold text-lg">{t('option_a')}</h3>
              <input
                required
                type="text"
                placeholder="Name"
                className="w-full bg-slate-950 border border-slate-700 rounded p-3 text-white"
                value={formData.optionAName}
                onChange={(e) => setFormData({ ...formData, optionAName: e.target.value })}
              />
              <OptionImageUpload
                id="option-a-image"
                image={formData.optionAImage}
                error={imageErrors.optionA}
                onUpload={(file) => handleImageUpload('A', file)}
              />
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  className="w-10 h-10 rounded cursor-pointer bg-slate-950 border-0"
                  value={formData.optionAColor}
                  onChange={(e) => setFormData({ ...formData, optionAColor: e.target.value })}
                />
                <span className="text-sm text-slate-400">Accent color (fallback)</span>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-bold text-lg">{t('option_b')}</h3>
              <input
                required
                type="text"
                placeholder="Name"
                className="w-full bg-slate-950 border border-slate-700 rounded p-3 text-white"
                value={formData.optionBName}
                onChange={(e) => setFormData({ ...formData, optionBName: e.target.value })}
              />
              <OptionImageUpload
                id="option-b-image"
                image={formData.optionBImage}
                error={imageErrors.optionB}
                onUpload={(file) => handleImageUpload('B', file)}
              />
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  className="w-10 h-10 rounded cursor-pointer bg-slate-950 border-0"
                  value={formData.optionBColor}
                  onChange={(e) => setFormData({ ...formData, optionBColor: e.target.value })}
                />
                <span className="text-sm text-slate-400">Accent color (fallback)</span>
              </div>
            </div>
          </div>

          <button
            type="submit"
            className="w-full py-4 bg-lime-400 text-slate-950 font-black uppercase tracking-widest rounded mt-8 hover:bg-lime-300 transition-colors"
          >
            {t('submit')}
          </button>
        </form>
      </div>
    </div>
  );
}
